// assets/byte-collection-showcase.js

if (!customElements.get('byte-collection-showcase')) {
  class ByteCollectionShowcase extends HTMLElement {
    constructor() {
      super();

      // Hover preview state
      this.activePreviewLink = null;
      this.isPreviewVisible = false;
      this.hasPreloadedPreviews = false;
      this.previewFrameId = null;
      this.pointerTargetX = 0;
      this.pointerTargetY = 0;
      this.previewX = 0;
      this.previewY = 0;
      this.previewWidth = 0;
      this.previewHeight = 0;
      this.previewEasing = 0.15;
      this.previewMaxTilt = 6;

      // Drag-to-scroll state
      this.isDragEnabled = false;
      this.isPointerDown = false;
      this.isDragging = false;
      this.shouldSuppressClick = false;
      this.dragPointerId = null;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.DRAG_THRESHOLD = 6;

      this.resizeObserver = null;
      this.finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.boundHandleListScroll = this.handleListScroll.bind(this);
      this.boundHandleListResize = this.handleListResize.bind(this);
      this.boundHandlePointerOver = this.handlePointerOver.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerLeave = this.handlePointerLeave.bind(this);
      this.boundHandleDragPointerDown = this.handleDragPointerDown.bind(this);
      this.boundHandleDragPointerMove = this.handleDragPointerMove.bind(this);
      this.boundHandleDragPointerUp = this.handleDragPointerUp.bind(this);
      this.boundHandleClickCapture = this.handleClickCapture.bind(this);
      this.boundHandleNativeDragStart = this.handleNativeDragStart.bind(this);
      this.boundUpdatePreviewPosition = this.#updatePreviewPosition.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.navList) return;

      this.#bindEvents();
      this.#centerActiveLink();
      this.#updateOverflowState();

      // Web fonts change link widths; re-measure once they are ready.
      document.fonts?.ready.then(() => {
        if (!this.isConnected) return;

        this.#centerActiveLink();
        this.#updateOverflowState();
      });
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#cancelPreviewFrame();
    }

    handleListScroll() {
      this.#updateOverflowState();
    }

    handleListResize() {
      this.#updateOverflowState();
    }

    handlePointerOver(event) {
      if (!this.#canShowPreview() || this.isDragging) return;

      const navLink = event.target.closest('[data-collection-nav-link]');
      if (!navLink || navLink === this.activePreviewLink) return;

      this.activePreviewLink = navLink;
      this.#preloadPreviewImages();
      this.#setPointerTarget(event.clientX, event.clientY);

      const previewSource = navLink.dataset.collectionNavPreviewSrc;

      if (!previewSource) {
        this.#hidePreview();
        return;
      }

      if (this.previewImage.getAttribute('src') !== previewSource) {
        this.previewImage.setAttribute('src', previewSource);
      }

      this.#showPreview();
    }

    handlePointerMove(event) {
      if (event.pointerType !== 'mouse' || !this.#canShowPreview()) return;

      this.#setPointerTarget(event.clientX, event.clientY);

      if (this.isPreviewVisible && this.previewFrameId === null) {
        this.previewFrameId = requestAnimationFrame(this.boundUpdatePreviewPosition);
      }
    }

    handlePointerLeave() {
      this.activePreviewLink = null;
      this.#hidePreview();
    }

    handleDragPointerDown(event) {
      if (!this.#canDrag() || event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.navList.scrollLeft;
    }

    handleDragPointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

      const dragDistance = event.clientX - this.dragStartX;

      if (!this.isDragging) {
        if (Math.abs(dragDistance) < this.DRAG_THRESHOLD) return;

        this.isDragging = true;
        this.navList.setPointerCapture(event.pointerId);
        this.navList.classList.add('is-dragging');
        this.activePreviewLink = null;
        this.#hidePreview();
      }

      this.navList.scrollLeft = this.dragStartScrollLeft - dragDistance;
    }

    handleDragPointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

      if (this.isDragging) {
        // The click that follows a drag must not open the link under the cursor.
        this.shouldSuppressClick = true;
        this.navList.classList.remove('is-dragging');

        if (this.navList.hasPointerCapture(event.pointerId)) {
          this.navList.releasePointerCapture(event.pointerId);
        }
      }

      this.isPointerDown = false;
      this.isDragging = false;
      this.dragPointerId = null;
    }

    handleClickCapture(event) {
      if (!this.shouldSuppressClick) return;

      this.shouldSuppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    handleNativeDragStart(event) {
      // Stop the browser's own link/image drag ghost from hijacking the gesture.
      if (this.#canDrag()) event.preventDefault();
    }

    #cacheElements() {
      this.navElement = this.querySelector('[data-collection-nav]');
      this.navList = this.querySelector('[data-collection-nav-list]');
      this.navLinks = this.querySelectorAll('[data-collection-nav-link]');
      this.previewElement = this.querySelector('[data-collection-nav-preview]');
      this.previewImage = this.querySelector('[data-collection-nav-preview-image]');

      const configuredEasing = Number.parseFloat(this.dataset.collectionNavEasing);
      const configuredTilt = Number.parseFloat(this.dataset.collectionNavTilt);

      if (Number.isFinite(configuredEasing)) {
        this.previewEasing = Math.min(Math.max(configuredEasing, 0.05), 1);
      }

      if (Number.isFinite(configuredTilt)) {
        this.previewMaxTilt = Math.max(configuredTilt, 0);
      }

      this.isDragEnabled = this.dataset.collectionNavDraggable === 'true';
    }

    #bindEvents() {
      this.navList.addEventListener('scroll', this.boundHandleListScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.boundHandleListResize);
        this.resizeObserver.observe(this.navList);
      }

      if (this.isDragEnabled) {
        this.navList.addEventListener('pointerdown', this.boundHandleDragPointerDown);
        this.navList.addEventListener('pointermove', this.boundHandleDragPointerMove);
        this.navList.addEventListener('pointerup', this.boundHandleDragPointerUp);
        this.navList.addEventListener('pointercancel', this.boundHandleDragPointerUp);
        this.navList.addEventListener('click', this.boundHandleClickCapture, true);
        this.navList.addEventListener('dragstart', this.boundHandleNativeDragStart);
      }

      if (!this.previewElement || !this.previewImage) return;

      this.navList.addEventListener('pointerover', this.boundHandlePointerOver);
      this.navList.addEventListener('pointermove', this.boundHandlePointerMove);
      this.navList.addEventListener('pointerleave', this.boundHandlePointerLeave);
    }

    #canShowPreview() {
      return Boolean(this.previewElement && this.previewImage && this.finePointerQuery.matches);
    }

    #canDrag() {
      return this.isDragEnabled && this.navList.classList.contains('can-drag');
    }

    #preloadPreviewImages() {
      if (this.hasPreloadedPreviews) return;

      this.hasPreloadedPreviews = true;

      for (const navLink of this.navLinks) {
        const previewSource = navLink.dataset.collectionNavPreviewSrc;
        if (!previewSource) continue;

        const preloadImage = new Image();
        preloadImage.decoding = 'async';
        preloadImage.src = previewSource;
      }
    }

    #centerActiveLink() {
      const activeLink = this.navList.querySelector('[aria-current="page"]');
      if (!activeLink) return;

      const hasOverflow = this.navList.scrollWidth > this.navList.clientWidth;
      if (!hasOverflow) return;

      const activeItem = activeLink.closest('li') || activeLink;
      const centeredScrollLeft =
        activeItem.offsetLeft - (this.navList.clientWidth - activeItem.offsetWidth) / 2;

      this.navList.scrollLeft = Math.max(0, centeredScrollLeft);
    }

    #updateOverflowState() {
      if (!this.navElement) return;

      const maxScrollLeft = this.navList.scrollWidth - this.navList.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      const currentScrollLeft = this.navList.scrollLeft;

      this.navElement.classList.toggle('has-overflow-start', hasOverflow && currentScrollLeft > 1);
      this.navElement.classList.toggle(
        'has-overflow-end',
        hasOverflow && currentScrollLeft < maxScrollLeft - 1
      );

      // Drag only makes sense for a mouse on a list that actually scrolls.
      this.navList.classList.toggle(
        'can-drag',
        this.isDragEnabled && hasOverflow && this.finePointerQuery.matches
      );
    }

    #setPointerTarget(clientX, clientY) {
      const viewportPadding = 16;
      const cursorOffset = 28;

      if (!this.previewWidth) {
        this.previewWidth = this.previewElement.offsetWidth;
        this.previewHeight = this.previewElement.offsetHeight;
      }

      const maxX = window.innerWidth - this.previewWidth - viewportPadding;
      const maxY = window.innerHeight - this.previewHeight - viewportPadding;

      this.pointerTargetX = Math.min(Math.max(clientX + cursorOffset, viewportPadding), maxX);
      this.pointerTargetY = Math.min(
        Math.max(clientY - this.previewHeight / 2, viewportPadding),
        maxY
      );
    }

    #showPreview() {
      if (this.isPreviewVisible) return;

      this.isPreviewVisible = true;

      // Start at the cursor so the preview never flies in from the corner.
      this.previewX = this.pointerTargetX;
      this.previewY = this.pointerTargetY;

      this.previewElement.classList.add('is-visible');

      if (this.previewFrameId === null) {
        this.previewFrameId = requestAnimationFrame(this.boundUpdatePreviewPosition);
      }
    }

    #hidePreview() {
      if (!this.previewElement || !this.isPreviewVisible) return;

      this.isPreviewVisible = false;
      this.previewElement.classList.remove('is-visible');
    }

    #updatePreviewPosition() {
      const isReducedMotion = this.reducedMotionQuery.matches;
      const easing = isReducedMotion ? 1 : this.previewEasing;

      const distanceX = this.pointerTargetX - this.previewX;
      const distanceY = this.pointerTargetY - this.previewY;

      this.previewX += distanceX * easing;
      this.previewY += distanceY * easing;

      // Tilt slightly in the direction of travel for a physical feel.
      const maxTilt = isReducedMotion ? 0 : this.previewMaxTilt;
      const tiltDegrees = Math.max(-maxTilt, Math.min(maxTilt, distanceX * 0.06));

      this.previewElement.style.transform =
        `translate3d(${this.previewX}px, ${this.previewY}px, 0) rotate(${tiltDegrees}deg)`;

      const isSettled = Math.abs(distanceX) < 0.2 && Math.abs(distanceY) < 0.2;

      if (this.isPreviewVisible && !isSettled) {
        this.previewFrameId = requestAnimationFrame(this.boundUpdatePreviewPosition);
        return;
      }

      this.previewFrameId = null;
    }

    #cancelPreviewFrame() {
      if (this.previewFrameId === null) return;

      cancelAnimationFrame(this.previewFrameId);
      this.previewFrameId = null;
    }

    #unbindEvents() {
      if (!this.navList) return;

      this.navList.removeEventListener('scroll', this.boundHandleListScroll);
      this.navList.removeEventListener('pointerover', this.boundHandlePointerOver);
      this.navList.removeEventListener('pointermove', this.boundHandlePointerMove);
      this.navList.removeEventListener('pointerleave', this.boundHandlePointerLeave);
      this.navList.removeEventListener('pointerdown', this.boundHandleDragPointerDown);
      this.navList.removeEventListener('pointermove', this.boundHandleDragPointerMove);
      this.navList.removeEventListener('pointerup', this.boundHandleDragPointerUp);
      this.navList.removeEventListener('pointercancel', this.boundHandleDragPointerUp);
      this.navList.removeEventListener('click', this.boundHandleClickCapture, true);
      this.navList.removeEventListener('dragstart', this.boundHandleNativeDragStart);

      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }
  }

  customElements.define('byte-collection-showcase', ByteCollectionShowcase);
}